import React, { lazy, memo } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, XCircle, Smartphone, Brain, Wallet, HeartCrack, Lightbulb, Bot, FlaskConical, Printer, Calendar, ClipboardList, Award, ShieldCheck, Clock, Zap, PlayCircle } from 'lucide-react';
import { CTAButton } from '../components/CTAButton';
import { FAQ } from '../components/FAQ';
import { Countdown } from '../components/Countdown';
const FadeIn = ({
  children,
  delay = 0
}: {
  children: React.ReactNode;
  delay?: number;
}) => <motion.div initial={{
  opacity: 0,
  y: 20
}} whileInView={{
  opacity: 1,
  y: 0
}} viewport={{
  once: true,
  margin: '-100px'
}} transition={{
  duration: 0.6,
  delay
}} data-id="element-19">
    {children}
  </motion.div>;
export function LandingPage() {
  return <div className="min-h-screen bg-slate-50 overflow-hidden" data-id="element-20">
      {/* HERO SECTION */}
      <section className="relative pt-2 pb-16 lg:pt-3 lg:pb-24 overflow-hidden bg-brand-950 text-white" data-id="element-21">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1502086223501-7ea6ecd79368?q=80&w=2038&auto=format&fit=crop')] bg-cover bg-center opacity-10 mix-blend-overlay" data-id="element-22"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-brand-950/50 to-brand-950" data-id="element-23"></div>

        <div className="container mx-auto px-4 relative z-10 max-w-5xl text-center" data-id="element-24">
          <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.5
        }} data-id="element-25">
            <span className="inline-block py-1 px-3 rounded-full bg-blue-500/20 text-blue-300 font-semibold text-sm md:text-base mb-3 border border-blue-500/30 pt-[1px] pb-[1px] pl-[12px] pr-[12px]" data-id="element-26">
              🧊 Edición Vacaciones de Invierno 2026
            </span>
            <h1 className="md:text-7xl mb-3 text-balance font-[900] text-[40px]" data-id="element-27">
              "Tus hijos van a pedirte el celular...{' '}
              <span className="text-accent-500" data-id="element-28">hasta que vean esto</span>"
            </h1>

            <motion.div initial={{
            opacity: 0,
            y: 40
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.3
          }} className="mb-5" data-id="element-29">
              <div className="relative mx-auto max-w-3xl" data-id="element-30">
                <div className="absolute -inset-3 bg-accent-500/20 blur-2xl rounded-[2rem]" data-id="element-31"></div>
                <img src="https://cdn.magicpatterns.com/uploads/jZWK5EYmVVDQFeMtJrMQC9/ChatGPT_Image_17_jun_2026,_06_38_12_p.m..png" alt="Familia armando los 3 kits del Kit Anti-Aburrimiento: robótica, experimentos científicos y papelería 3D" className="relative w-full rounded-2xl md:rounded-3xl shadow-2xl ring-1 ring-white/10" loading="eager" data-id="element-32" />
              </div>
            </motion.div>

            <p className="text-xl md:text-2xl text-blue-100 mb-5 max-w-3xl mx-auto leading-relaxed" data-id="element-33">
              La herramienta S.O.S para padres argentinos que quieren sobrevivir
              las vacaciones de invierno{' '}
              <span className="font-bold text-white border-b-2 border-accent-500" data-id="element-34">
                sin pantallas, sin pelear y sin gastar una fortuna.
              </span>
            </p>

            <div className="flex flex-wrap justify-center md:gap-4 mb-6 text-lg font-medium text-blue-50 gap-[5px]" data-id="element-35">
              <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full backdrop-blur-sm" data-id="element-36">
                <CheckCircle2 className="text-green-400 w-5 h-5" data-id="element-37" /> Robótica
                casera
              </div>
              <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full backdrop-blur-sm" data-id="element-38">
                <CheckCircle2 className="text-green-400 w-5 h-5" data-id="element-39" /> Experimentos
                científicos
              </div>
              <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full backdrop-blur-sm" data-id="element-40">
                <CheckCircle2 className="text-green-400 w-5 h-5" data-id="element-41" /> Papercraft
                3D
              </div>
              <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full backdrop-blur-sm" data-id="element-42">
                <CheckCircle2 className="text-green-400 w-5 h-5" data-id="element-43" /> +1.500
                proyectos
              </div>
            </div>

            <CTAButton data-id="element-44" />
          </motion.div>
        </div>
      </section>

      {/* SECCIÓN 1: EL DOLOR */}
      <section className="py-12 md:py-16 bg-white pt-[40px] pb-[40px]" data-id="element-45">
        <div className="container mx-auto px-4 max-w-3xl" data-id="element-46">
          <FadeIn data-id="element-47">
            <h2 className="text-3xl md:text-4xl font-black text-slate-900 mb-4 text-center" data-id="element-48">
              Son las 10 de la mañana del primer día de vacaciones.
            </h2>
            <div className="prose prose-lg md:prose-xl text-slate-700 mx-auto" data-id="element-49">
              <p data-id="element-50">Y ya escuchaste la frase más temida de la crianza moderna:</p>
              <img src="https://cdn.magicpatterns.com/uploads/hPArv6w3fS6comquqnqT6Z/heylloyd-image-1781733530297.png" alt="Niño aburrido con el celular colgando mientras sus padres trabajan en la cocina, con un globo de pensamiento que dice 'Estoy aburrido'" className="w-full rounded-2xl shadow-xl my-4 not-prose" loading="lazy" data-id="element-51" />
              <blockquote className="border-l-4 border-accent-500 pl-6 italic font-display text-2xl text-slate-900 my-4 bg-slate-50 p-6 rounded-r-xl" data-id="element-52">
                "Papá/Mamá... me aburro. ¿Puedo usar el celu?"
              </blockquote>
              <p data-id="element-53">
                Ahí arranca la negociación silenciosa que todos los padres
                conocemos.
              </p>
              <p data-id="element-54">
                Le decís que no. Te mira con cara de tragedia. Le decís "bueno,
                un ratito". El ratito se convierte en la tarde entera. Y vos
                terminás el día con esa mezcla rara de culpa y agotamiento que
                no tiene nombre pero que todos los padres sentimos.
              </p>
              <div className="bg-red-50 border border-red-100 rounded-2xl p-6 my-5" data-id="element-55">
                <p className="font-semibold text-red-900 m-0" data-id="element-56">
                  No es que tus hijos sean vagos. No es que vos seas mal padre o
                  mala madre.
                </p>
                <p className="text-red-800 mt-3 m-0" data-id="element-57">
                  Es que las pantallas están diseñadas por los mejores
                  ingenieros del mundo para que nadie pueda dejar de usarlas.
                  Son básicamente imposibles de competir...{' '}
                  <strong className="font-black" data-id="element-58">
                    si no tenés la herramienta correcta.
                  </strong>
                </p>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* SECCIÓN 2: AGITACIÓN */}
      <section className="py-12 md:py-16 bg-slate-100" data-id="element-59">
        <div className="container mx-auto px-4 max-w-4xl" data-id="element-60">
          <FadeIn data-id="element-61">
            <h2 className="text-3xl md:text-4xl font-black text-center mb-8" data-id="element-62">
              Pensá en lo que pasa si estas vacaciones son{' '}
              <span className="text-red-600" data-id="element-63">iguales a siempre...</span>
            </h2>

            <div className="grid md:grid-cols-2 gap-4 mb-8" data-id="element-64">
              <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200 flex gap-4" data-id="element-65">
                <Smartphone className="w-8 h-8 text-slate-400 flex-shrink-0" data-id="element-66" />
                <div data-id="element-67">
                  <h3 className="font-bold text-xl mb-1" data-id="element-68">
                    14 días de celular y tele
                  </h3>
                  <p className="text-slate-600" data-id="element-69">
                    Tus hijos llegan al colegio sin haber hecho nada memorable.
                  </p>
                </div>
              </div>
              <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200 flex gap-4" data-id="element-70">
                <Brain className="w-8 h-8 text-slate-400 flex-shrink-0" data-id="element-71" />
                <div data-id="element-72">
                  <h3 className="font-bold text-xl mb-1" data-id="element-73">
                    La frustración acumulada
                  </h3>
                  <p className="text-slate-600" data-id="element-74">
                    Cada vez que les decís "apagá la pantalla", hay un
                    conflicto.
                  </p>
                </div>
              </div>
              <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200 flex gap-4" data-id="element-75">
                <Wallet className="w-8 h-8 text-slate-400 flex-shrink-0" data-id="element-76" />
                <div data-id="element-77">
                  <h3 className="font-bold text-xl mb-1" data-id="element-78">
                    La presión económica
                  </h3>
                  <p className="text-slate-600" data-id="element-79">
                    Salir a todos lados cuesta caro. El clima no ayuda. Y vos no
                    podés tomar licencia dos semanas seguidas.
                  </p>
                </div>
              </div>
              <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200 flex gap-4" data-id="element-80">
                <HeartCrack className="w-8 h-8 text-slate-400 flex-shrink-0" data-id="element-81" />
                <div data-id="element-82">
                  <h3 className="font-bold text-xl mb-1" data-id="element-83">
                    La culpa que no se va
                  </h3>
                  <p className="text-slate-600" data-id="element-84">
                    Esa vocecita que dice: "Deberían estar haciendo algo más
                    productivo con su tiempo".
                  </p>
                </div>
              </div>
            </div>

            <div className="text-center max-w-2xl mx-auto" data-id="element-85">
              <p className="text-xl text-slate-700 mb-3" data-id="element-86">
                Y lo peor de todo no es el celular. Lo peor es la{' '}
                <strong className="text-slate-900" data-id="element-87">oportunidad perdida.</strong>
              </p>
              <p className="text-xl text-slate-700 mb-4" data-id="element-88">
                Porque las vacaciones de invierno de 2026 no van a volver. Y la
                pregunta que más duele no es "¿qué hicieron tus hijos estas
                vacaciones?"
              </p>
              <p className="text-2xl md:text-3xl font-black text-brand-900 bg-brand-100 py-4 px-6 rounded-2xl inline-block" data-id="element-89">
                La pregunta que más duele es: ¿los viste crear algo con sus
                propias manos?
              </p>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* SECCIÓN 3: EL GIRO / MECANISMO */}
      <section className="py-12 md:py-16 bg-brand-900 text-white relative overflow-hidden" data-id="element-90">
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden opacity-10 pointer-events-none" data-id="element-91">
          <div className="absolute -top-[20%] -left-[10%] w-[50%] h-[50%] rounded-full bg-brand-500 blur-[120px]" data-id="element-92"></div>
          <div className="absolute top-[60%] -right-[10%] w-[50%] h-[50%] rounded-full bg-accent-500 blur-[120px]" data-id="element-93"></div>
        </div>

        <div className="container mx-auto px-4 max-w-3xl relative z-10" data-id="element-94">
          <FadeIn data-id="element-95">
            <div className="flex justify-center mb-4" data-id="element-96">
              <Lightbulb className="w-12 h-12 text-yellow-400" data-id="element-97" />
            </div>
            <h2 className="text-3xl md:text-5xl font-black text-center mb-5 leading-tight" data-id="element-98">
              La verdad que nadie te dice sobre el aburrimiento de tus hijos
            </h2>
            <div className="text-xl md:text-2xl text-brand-100 space-y-3 text-center" data-id="element-99">
              <p data-id="element-100">
                Tus hijos{' '}
                <strong className="text-white" data-id="element-101">
                  no están aburridos de actividades.
                </strong>
              </p>
              <p data-id="element-102">Están aburridos porque nadie les abrió la puerta correcta.</p>
            </div>

            <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-3xl p-6 md:p-8 mt-6" data-id="element-103">
              <p className="text-lg md:text-xl leading-relaxed mb-3" data-id="element-104">
                Cuando un chico construye un robot con sus propias manos, cuando
                hace explotar un volcán en la cocina, cuando arma a su jugador
                favorito en papel 3D y lo puede mostrar con orgullo...
              </p>
              <p className="text-2xl md:text-3xl font-bold text-yellow-400 mb-4" data-id="element-105">
                Su cerebro libera la misma dopamina que busca en el teléfono.
              </p>
              <p className="text-lg md:text-xl leading-relaxed mb-4" data-id="element-106">
                Pero con una diferencia enorme: esta dopamina viene con un logro
                real. Con algo que pueden tocar, mostrar y decir:{' '}
                <strong className="text-white" data-id="element-107">"Lo hice yo."</strong>
              </p>
              <img src="https://cdn.magicpatterns.com/uploads/r8qJms3RSSUUZp58w1S6zP/ChatGPT_Image_19_jun_2026,_05_05_59_p.m..png" alt="Niño sonriente señalando con orgullo el robot de cartón que construyó con sus propias manos" className="w-full rounded-2xl shadow-xl mb-4" loading="lazy" data-id="element-108" />
              <div className="bg-accent-500 text-white font-black text-2xl md:text-3xl py-4 px-6 rounded-2xl text-center transform -rotate-1 shadow-xl" data-id="element-109">
                A eso le llamamos el Efecto Manos-a-la-Obra.
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* SECCIÓN 4: PRESENTACIÓN DEL PRODUCTO */}
      <section className="py-12 md:py-16 bg-white" id="producto" data-id="element-110">
        <div className="container mx-auto px-4 max-w-5xl" data-id="element-111">
          <FadeIn data-id="element-112">
            <div className="text-center mb-10" data-id="element-113">
              <span className="inline-block py-1 px-3 rounded-full bg-brand-100 text-brand-700 font-bold tracking-wider text-sm mb-3 uppercase" data-id="element-114">
                Te presentamos
              </span>
              <h2 className="text-4xl md:text-6xl font-black text-slate-900 mb-3" data-id="element-115">
                EL KIT ANTI-ABURRIMIENTO
              </h2>
              <p className="text-xl text-slate-600 max-w-3xl mx-auto" data-id="element-116">
                El pack más completo de actividades creativas, científicas y
                manuales diseñado específicamente para que tus hijos tengan las
                mejores vacaciones de invierno de su vida —{' '}
                <strong className="text-slate-900" data-id="element-117">
                  sin salir de casa y sin tocar el teléfono.
                </strong>
              </p>
            </div>

            <div className="relative mx-auto max-w-2xl mb-10" data-id="element-118">
              <div className="absolute -inset-4 bg-brand-500/10 blur-3xl rounded-[2.5rem]" data-id="element-119"></div>
              <img src="https://cdn.magicpatterns.com/uploads/pZeYEn995SZXJDF2TcMYfX/ChatGPT_Image_17_jun_2026,_07_09_37_p.m..png" alt="Caja del Kit Anti-Aburrimiento con los 3 materiales: Robótica, Experimentos científicos y Papelería 3D" className="relative w-full rounded-3xl shadow-2xl" loading="lazy" data-id="element-120" />
            </div>

            <h3 className="text-3xl font-black text-center mb-6" data-id="element-121">
              📦 ¿QUÉ INCLUYE EL KIT COMPLETO?
            </h3>

            {/* Material 1 */}
            <div className="bg-slate-50 rounded-3xl border border-slate-200 overflow-hidden mb-6 shadow-sm" data-id="element-122">
              <div className="bg-brand-600 p-5 md:p-6 text-white flex items-center gap-4" data-id="element-123">
                <div className="bg-white/20 p-3 rounded-2xl" data-id="element-124">
                  <Bot className="w-8 h-8" data-id="element-125" />
                </div>
                <div data-id="element-126">
                  <h4 className="text-xl md:text-2xl font-black" data-id="element-127">
                    MATERIAL #1 — PEQUEÑOS INGENIEROS
                  </h4>
                  <p className="text-brand-100 mt-1" data-id="element-128">
                    20 Proyectos de Robótica para Niños
                  </p>
                </div>
              </div>
              <div className="p-5 md:p-6" data-id="element-129">
                <div className="overflow-x-auto" data-id="element-130">
                  <table className="w-full text-left border-collapse" data-id="element-131">
                    <thead data-id="element-132">
                      <tr className="border-b-2 border-slate-200" data-id="element-133">
                        <th className="pb-2 font-bold text-slate-900" data-id="element-134">
                          Lo que hacen
                        </th>
                        <th className="pb-2 font-bold text-slate-900" data-id="element-135">
                          Lo que aprenden
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100" data-id="element-136">
                      <tr data-id="element-137">
                        <td className="py-3 text-slate-700" data-id="element-138">
                          Arman robots con materiales reciclados
                        </td>
                        <td className="py-3 font-medium text-brand-700" data-id="element-139">
                          Lógica y resolución de problemas
                        </td>
                      </tr>
                      <tr data-id="element-140">
                        <td className="py-3 text-slate-700" data-id="element-141">
                          Construyen mecanismos que se mueven
                        </td>
                        <td className="py-3 font-medium text-brand-700" data-id="element-142">
                          Física básica sin darse cuenta
                        </td>
                      </tr>
                      <tr data-id="element-143">
                        <td className="py-3 text-slate-700" data-id="element-144">
                          Programan movimientos simples
                        </td>
                        <td className="py-3 font-medium text-brand-700" data-id="element-145">
                          Introducción al pensamiento computacional
                        </td>
                      </tr>
                      <tr data-id="element-146">
                        <td className="py-3 text-slate-700" data-id="element-147">
                          Muestran su creación con orgullo
                        </td>
                        <td className="py-3 font-medium text-brand-700" data-id="element-148">
                          Autoestima y confianza
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div className="mt-4 flex flex-wrap gap-3 text-sm font-medium text-slate-600 bg-white p-3 rounded-xl border border-slate-100" data-id="element-149">
                  <span className="flex items-center gap-1" data-id="element-150">
                    <CheckCircle2 className="w-4 h-4 text-green-500" data-id="element-151" /> Desde 6
                    años
                  </span>
                  <span className="flex items-center gap-1" data-id="element-152">
                    <CheckCircle2 className="w-4 h-4 text-green-500" data-id="element-153" />{' '}
                    Materiales de casa
                  </span>
                  <span className="flex items-center gap-1" data-id="element-154">
                    <CheckCircle2 className="w-4 h-4 text-green-500" data-id="element-155" /> Sin
                    electricidad/peligro
                  </span>
                </div>
              </div>
            </div>

            {/* Material 2 */}
            <div className="bg-slate-50 rounded-3xl border border-slate-200 overflow-hidden mb-6 shadow-sm" data-id="element-156">
              <div className="bg-emerald-600 p-5 md:p-6 text-white flex items-center gap-4" data-id="element-157">
                <div className="bg-white/20 p-3 rounded-2xl" data-id="element-158">
                  <FlaskConical className="w-8 h-8" data-id="element-159" />
                </div>
                <div data-id="element-160">
                  <h4 className="text-xl md:text-2xl font-black" data-id="element-161">
                    MATERIAL #2 — LABORATORIO EN CASA
                  </h4>
                  <p className="text-emerald-100 mt-1" data-id="element-162">
                    Más de 100 Experimentos Científicos
                  </p>
                </div>
              </div>
              <div className="p-5 md:p-6" data-id="element-163">
                <div className="grid md:grid-cols-2 gap-4 mb-4" data-id="element-164">
                  <div className="bg-white p-4 rounded-2xl border border-slate-100 shadow-sm" data-id="element-165">
                    <h5 className="font-bold mb-1 flex items-center gap-2" data-id="element-166">
                      🔬 Química
                    </h5>
                    <p className="text-slate-600 text-sm" data-id="element-167">
                      Volcán de bicarbonato, slime casero, tinta invisible
                    </p>
                  </div>
                  <div className="bg-white p-4 rounded-2xl border border-slate-100 shadow-sm" data-id="element-168">
                    <h5 className="font-bold mb-1 flex items-center gap-2" data-id="element-169">
                      ⚡ Física
                    </h5>
                    <p className="text-slate-600 text-sm" data-id="element-170">
                      Globos que se inflan solos, imanes improvisados
                    </p>
                  </div>
                  <div className="bg-white p-4 rounded-2xl border border-slate-100 shadow-sm" data-id="element-171">
                    <h5 className="font-bold mb-1 flex items-center gap-2" data-id="element-172">
                      🌊 Biología
                    </h5>
                    <p className="text-slate-600 text-sm" data-id="element-173">
                      Cristales que crecen, plantas que cambian de color
                    </p>
                  </div>
                  <div className="bg-white p-4 rounded-2xl border border-slate-100 shadow-sm" data-id="element-174">
                    <h5 className="font-bold mb-1 flex items-center gap-2" data-id="element-175">
                      🎨 Arte Científico
                    </h5>
                    <p className="text-slate-600 text-sm" data-id="element-176">
                      Pinturas que explotan, dibujos que cobran vida
                    </p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-3 text-sm font-medium text-slate-600 bg-white p-3 rounded-xl border border-slate-100" data-id="element-177">
                  <span className="flex items-center gap-1" data-id="element-178">
                    <PlayCircle className="w-4 h-4 text-emerald-500" data-id="element-179" /> +40
                    videos paso a paso
                  </span>
                  <span className="flex items-center gap-1" data-id="element-180">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500" data-id="element-181" />{' '}
                    Ingredientes de cocina
                  </span>
                  <span className="flex items-center gap-1" data-id="element-182">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500" data-id="element-183" />{' '}
                    Explicaciones simples
                  </span>
                </div>
              </div>
            </div>

            {/* Material 3 */}
            <div className="bg-slate-50 rounded-3xl border border-slate-200 overflow-hidden mb-8 shadow-sm" data-id="element-184">
              <div className="bg-purple-600 p-5 md:p-6 text-white flex items-center gap-4" data-id="element-185">
                <div className="bg-white/20 p-3 rounded-2xl" data-id="element-186">
                  <Printer className="w-8 h-8" data-id="element-187" />
                </div>
                <div data-id="element-188">
                  <h4 className="text-xl md:text-2xl font-black" data-id="element-189">
                    MATERIAL #3 — FÁBRICA DE JUGUETES 3D
                  </h4>
                  <p className="text-purple-100 mt-1" data-id="element-190">
                    El MegaPack más completo de Papercraft
                  </p>
                </div>
              </div>
              <div className="p-5 md:p-6" data-id="element-191">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4" data-id="element-192">
                  <div className="bg-white p-3 rounded-xl border border-slate-100 text-center" data-id="element-193">
                    <div className="text-2xl mb-1" data-id="element-194">📦</div>
                    <div className="font-bold text-slate-900 text-sm" data-id="element-195">
                      +1.500 modelos
                    </div>
                  </div>
                  <div className="bg-white p-3 rounded-xl border border-slate-100 text-center" data-id="element-196">
                    <div className="text-2xl mb-1" data-id="element-197">⚽</div>
                    <div className="font-bold text-slate-900 text-sm" data-id="element-198">
                      Figuras de Fútbol
                    </div>
                  </div>
                  <div className="bg-white p-3 rounded-xl border border-slate-100 text-center" data-id="element-199">
                    <div className="text-2xl mb-1" data-id="element-200">🎮</div>
                    <div className="font-bold text-slate-900 text-sm" data-id="element-201">
                      Personajes Pop
                    </div>
                  </div>
                  <div className="bg-white p-3 rounded-xl border border-slate-100 text-center" data-id="element-202">
                    <div className="text-2xl mb-1" data-id="element-203">🦁</div>
                    <div className="font-bold text-slate-900 text-sm" data-id="element-204">
                      Mundo Animal
                    </div>
                  </div>
                  <div className="bg-white p-3 rounded-xl border border-slate-100 text-center" data-id="element-205">
                    <div className="text-2xl mb-1" data-id="element-206">🚀</div>
                    <div className="font-bold text-slate-900 text-sm" data-id="element-207">
                      Ciencia Ficción
                    </div>
                  </div>
                  <div className="bg-white p-3 rounded-xl border border-slate-100 text-center" data-id="element-208">
                    <div className="text-2xl mb-1" data-id="element-209">🏰</div>
                    <div className="font-bold text-slate-900 text-sm" data-id="element-210">
                      Fantasía
                    </div>
                  </div>
                </div>
                <div className="flex flex-wrap gap-3 text-sm font-medium text-slate-600 bg-white p-3 rounded-xl border border-slate-100" data-id="element-211">
                  <span className="flex items-center gap-1" data-id="element-212">
                    <CheckCircle2 className="w-4 h-4 text-purple-500" data-id="element-213" /> Listas
                    para imprimir
                  </span>
                  <span className="flex items-center gap-1" data-id="element-214">
                    <CheckCircle2 className="w-4 h-4 text-purple-500" data-id="element-215" /> Guías
                    desde 5 años
                  </span>
                  <span className="flex items-center gap-1" data-id="element-216">
                    <CheckCircle2 className="w-4 h-4 text-purple-500" data-id="element-217" /> 28
                    proyectos extra
                  </span>
                </div>
              </div>
            </div>

            {/* Bonuses */}
            <div className="relative" data-id="element-218">
              <div className="absolute inset-0 bg-gradient-to-b from-yellow-50 to-white rounded-3xl transform -skew-y-1" data-id="element-219"></div>
              <div className="relative z-10 p-6 md:p-8 border-2 border-yellow-200 rounded-3xl" data-id="element-220">
                <h3 className="text-2xl font-black text-center mb-6 flex items-center justify-center gap-2" data-id="element-221">
                  <span className="text-3xl" data-id="element-222">🎁</span> BONUS ESPECIALES
                </h3>

                <div className="grid md:grid-cols-3 gap-4" data-id="element-223">
                  <div className="bg-white p-5 rounded-2xl shadow-sm border border-yellow-100 relative pt-8" data-id="element-224">
                    <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-yellow-400 text-yellow-900 font-bold py-1 px-3 rounded-full text-xs" data-id="element-225">
                      BONUS #1
                    </div>
                    <Calendar className="w-8 h-8 text-yellow-500 mb-2 mx-auto" data-id="element-226" />
                    <h4 className="font-bold text-lg text-center mb-2" data-id="element-227">
                      Planificador de las 2 Semanas
                    </h4>
                    <p className="text-slate-600 text-sm text-center" data-id="element-228">
                      Un calendario visual listo para completar. Tus hijos lo
                      llenan y cada mañana saben qué hacer. Adiós al "no sé qué
                      hacer".
                    </p>
                  </div>

                  <div className="bg-white p-5 rounded-2xl shadow-sm border border-yellow-100 relative pt-8" data-id="element-229">
                    <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-yellow-400 text-yellow-900 font-bold py-1 px-3 rounded-full text-xs" data-id="element-230">
                      BONUS #2
                    </div>
                    <ClipboardList className="w-8 h-8 text-yellow-500 mb-2 mx-auto" data-id="element-231" />
                    <h4 className="font-bold text-lg text-center mb-2" data-id="element-232">
                      Lista de Materiales Low-Cost
                    </h4>
                    <p className="text-slate-600 text-sm text-center" data-id="element-233">
                      Todo lo que necesitás. El 90% ya está en tu casa. El 10%
                      restante no cuesta más que un café.
                    </p>
                  </div>

                  <div className="bg-white p-5 rounded-2xl shadow-sm border border-yellow-100 relative pt-8" data-id="element-234">
                    <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-yellow-400 text-yellow-900 font-bold py-1 px-3 rounded-full text-xs" data-id="element-235">
                      BONUS #3
                    </div>
                    <Award className="w-8 h-8 text-yellow-500 mb-2 mx-auto" data-id="element-236" />
                    <h4 className="font-bold text-lg text-center mb-2" data-id="element-237">
                      Diploma del Pequeño Inventor
                    </h4>
                    <p className="text-slate-600 text-sm text-center" data-id="element-238">
                      Al terminar, tu hijo recibe su diploma oficial para
                      imprimir y enmarcar. Porque cada logro merece ser
                      celebrado.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-8 text-center" data-id="element-239">
              <CTAButton data-id="element-240" />
            </div>
          </FadeIn>
        </div>
      </section>

      {/* SECCIÓN 5: BENEFICIOS */}
      <section className="py-12 md:py-16 bg-slate-900 text-white" data-id="element-241">
        <div className="container mx-auto px-4 max-w-4xl" data-id="element-242">
          <FadeIn data-id="element-243">
            <h2 className="text-3xl md:text-4xl font-black text-center mb-8" data-id="element-244">
              Lo que{' '}
              <span className="text-accent-500" data-id="element-245">realmente te llevás</span> con
              este kit:
            </h2>

            <div className="grid gap-3" data-id="element-246">
              {[{
              b: '100+ experimentos listos',
              m: 'No tenés que inventar nada. Abrís el kit y empezás'
            }, {
              b: 'Actividades para todas las edades',
              m: 'Sirve para todos tus hijos al mismo tiempo'
            }, {
              b: 'Materiales que ya tenés en casa',
              m: 'Cero gasto extra. Cero viaje al bazar'
            }, {
              b: 'Instrucciones paso a paso',
              m: 'Tus hijos pueden hacerlo solos. Vos podés trabajar tranquilo'
            }, {
              b: '+1.500 modelos de papercraft',
              m: 'Si terminan uno, hay 1.499 más esperando'
            }, {
              b: 'Acceso inmediato y digital',
              m: 'Lo comprás hoy y lo usás en 5 minutos'
            }].map((item, i) => <div key={i} className="flex flex-col md:flex-row items-start md:items-center gap-3 bg-slate-800/50 p-4 rounded-2xl border border-slate-700" data-id="element-247">
                  <div className="flex-1 font-bold text-lg text-brand-300 flex items-center gap-2" data-id="element-248">
                    <CheckCircle2 className="w-5 h-5 text-accent-500 flex-shrink-0" data-id="element-249" />
                    {item.b}
                  </div>
                  <div className="hidden md:block text-slate-600" data-id="element-250">→</div>
                  <div className="flex-1 text-slate-300" data-id="element-251">{item.m}</div>
                </div>)}
            </div>
          </FadeIn>
        </div>
      </section>

      {/* SECCIÓN 6: PRUEBA Y AUTORIDAD */}
      <section className="py-12 md:py-16 bg-white" data-id="element-252">
        <div className="container mx-auto px-4 max-w-4xl" data-id="element-253">
          <FadeIn data-id="element-254">
            <h2 className="text-3xl md:text-4xl font-black text-center mb-8" data-id="element-255">
              ¿Por qué esto funciona cuando todo lo demás falla?
            </h2>

            <div className="grid md:grid-cols-2 gap-6 items-center" data-id="element-256">
              <div className="space-y-4" data-id="element-257">
                <div className="flex gap-3 items-start opacity-50" data-id="element-258">
                  <XCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-1" data-id="element-259" />
                  <div data-id="element-260">
                    <h4 className="font-bold line-through" data-id="element-261">YouTube</h4>
                    <p className="text-sm" data-id="element-262">
                      Más pantalla. Pasivos, consumiendo, sin crear nada.
                    </p>
                  </div>
                </div>
                <div className="flex gap-3 items-start opacity-50" data-id="element-263">
                  <XCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-1" data-id="element-264" />
                  <div data-id="element-265">
                    <h4 className="font-bold line-through" data-id="element-266">
                      Juguetes del kiosco
                    </h4>
                    <p className="text-sm" data-id="element-267">
                      Se aburren en 20 min. El packaging prometía más.
                    </p>
                  </div>
                </div>
                <div className="flex gap-3 items-start opacity-50" data-id="element-268">
                  <XCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-1" data-id="element-269" />
                  <div data-id="element-270">
                    <h4 className="font-bold line-through" data-id="element-271">
                      Salir a pasear todos los días
                    </h4>
                    <p className="text-sm" data-id="element-272">
                      El frío, el costo, los horarios no siempre lo permiten.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-brand-50 p-6 rounded-3xl border border-brand-100" data-id="element-273">
                <h3 className="font-black text-xl mb-4 text-brand-900" data-id="element-274">
                  El KIT funciona porque:
                </h3>
                <ul className="space-y-3" data-id="element-275">
                  <li className="flex gap-2" data-id="element-276">
                    <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0" data-id="element-277" />
                    <span className="text-slate-700 text-sm" data-id="element-278">
                      <strong data-id="element-279">Es activo, no pasivo.</strong> Hacen, construyen,
                      experimentan. No consumen.
                    </span>
                  </li>
                  <li className="flex gap-2" data-id="element-280">
                    <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0" data-id="element-281" />
                    <span className="text-slate-700 text-sm" data-id="element-282">
                      <strong data-id="element-283">Resultados visibles.</strong> Al final del día
                      tienen un robot, un volcán, una figura.
                    </span>
                  </li>
                  <li className="flex gap-2" data-id="element-284">
                    <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0" data-id="element-285" />
                    <span className="text-slate-700 text-sm" data-id="element-286">
                      <strong data-id="element-287">Activa el orgullo.</strong> "Lo hice yo" es el
                      mejor antídoto contra el aburrimiento.
                    </span>
                  </li>
                  <li className="flex gap-2" data-id="element-288">
                    <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0" data-id="element-289" />
                    <span className="text-slate-700 text-sm" data-id="element-290">
                      <strong data-id="element-291">Diseñado para Argentina.</strong> Materiales
                      fáciles, para hacer adentro.
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* SECCIÓN 7: OFERTA STACK */}
      <section className="py-12 md:py-16 bg-brand-900 text-white relative" data-id="element-292">
        <div className="container mx-auto px-4 max-w-3xl" data-id="element-293">
          <FadeIn data-id="element-294">
            <div className="bg-white text-slate-900 rounded-3xl shadow-2xl overflow-hidden" data-id="element-295">
              <div className="bg-accent-500 text-white text-center py-4" data-id="element-296">
                <h2 className="text-2xl font-black" data-id="element-297">
                  📦 TODO LO QUE RECIBÍS HOY
                </h2>
              </div>
              <div className="p-6 md:p-8" data-id="element-298">
                <ul className="space-y-3 mb-6" data-id="element-299">
                  {[{
                  item: 'Material #1: 20 Proyectos de Robótica',
                  price: 'US$27'
                }, {
                  item: 'Material #2: +100 Experimentos Científicos + 40 Videos',
                  price: 'US$37'
                }, {
                  item: 'Material #3: Fábrica de Juguetes 3D (+1.500 modelos)',
                  price: 'US$47'
                }, {
                  item: 'Bonus #1: Planificador de 2 Semanas',
                  price: 'US$17'
                }, {
                  item: 'Bonus #2: Lista de Materiales Low-Cost',
                  price: 'US$12'
                }, {
                  item: 'Bonus #3: Diploma del Pequeño Inventor',
                  price: 'US$9'
                }, {
                  item: 'Acceso inmediato y descarga digital',
                  price: 'US$0'
                }].map(({
                  item,
                  price
                }, i) => <li key={i} className="flex items-center justify-between gap-3 border-b border-slate-100 pb-3 last:border-0" data-id="element-300">
                      <span className="font-medium flex items-center gap-2" data-id="element-301">
                        <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" data-id="element-302" />{' '}
                        {item}
                      </span>
                      <span className="flex items-center gap-2 flex-shrink-0" data-id="element-303">
                        <span className="text-slate-400 line-through text-sm" data-id="element-304">
                          {price}
                        </span>
                        <span className="font-bold text-green-600 bg-green-50 px-2 py-1 rounded-full text-xs" data-id="element-305">
                          GRATIS
                        </span>
                      </span>
                    </li>)}
                </ul>

                {/* PRICE STACK / OFERTA DE LANZAMIENTO */}
                <div className="bg-slate-50 rounded-2xl border border-slate-200 p-6 mb-6" data-id="element-306">
                  <div className="flex items-center justify-between pb-3 mb-3 border-b border-slate-200" data-id="element-307">
                    <span className="font-bold text-slate-700" data-id="element-308">
                      Valor total real:
                    </span>
                    <span className="text-2xl font-black text-slate-400 line-through" data-id="element-309">
                      US$149
                    </span>
                  </div>
                  <div className="text-center" data-id="element-310">
                    <p className="inline-flex items-center gap-2 bg-red-100 text-red-700 font-bold text-xs px-3 py-1 rounded-full mb-3 uppercase tracking-wide" data-id="element-311">
                      🔥 Oferta de lanzamiento
                    </p>
                    <p className="text-slate-600 font-medium mb-1" data-id="element-312">
                      Hoy, todo el kit + los 3 bonus por:
                    </p>
                    <div className="flex items-end justify-center gap-2" data-id="element-313">
                      <span className="text-6xl md:text-7xl font-black text-accent-500 leading-none" data-id="element-314">
                        US$9
                      </span>
                    </div>
                    <p className="text-sm text-green-600 font-bold mt-2" data-id="element-315">
                      Ahorrás US$140 (94% de descuento)
                    </p>
                    <p className="text-xs text-slate-500 mt-1" data-id="element-316">
                      Pago único · Sin suscripciones · Acceso de por vida
                    </p>
                  </div>
                </div>

                <div className="text-center bg-brand-50 p-6 rounded-2xl border border-brand-100 mb-6" data-id="element-317">
                  <p className="text-lg font-medium text-slate-600 mb-2" data-id="element-318">
                    💬 Una sola pregunta:
                  </p>
                  <p className="text-xl md:text-2xl font-black text-slate-900 mb-3 leading-tight" data-id="element-319">
                    ¿Cuánto vale para vos tener 14 días de vacaciones en paz,
                    viendo a tus hijos crear, reírse y aprender?
                  </p>
                  <p className="text-slate-600" data-id="element-320">
                    Este kit cuesta{' '}
                    <strong className="text-slate-900" data-id="element-321">
                      menos que llevar a tus hijos al cine una sola vez.
                    </strong>{' '}
                    Y dura las dos semanas completas.
                  </p>
                </div>

                <div className="text-center" data-id="element-322">
                  <CTAButton text="SÍ, QUIERO EL KIT ANTI-ABURRIMIENTO" className="w-full md:w-auto" data-id="element-323" />
                </div>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* SECCIÓN 8: RISK REVERSAL */}
      <section className="py-10 md:py-14 bg-slate-100" data-id="element-324">
        <div className="container mx-auto px-4 max-w-3xl text-center" data-id="element-325">
          <FadeIn data-id="element-326">
            <ShieldCheck className="w-16 h-16 text-green-500 mx-auto mb-4" data-id="element-327" />
            <h2 className="text-2xl font-black mb-4 text-slate-900" data-id="element-328">
              Garantía Total: Si no los entretiene, no lo pagás
            </h2>
            <p className="text-slate-700 mb-3" data-id="element-329">
              Sé que comprás sin verlo. Sé que es una decisión de confianza.
            </p>
            <p className="text-slate-700 mb-3" data-id="element-330">
              Por eso te ofrezco esto: si abrís el kit, lo usás con tus hijos y
              no te parece que vale cada peso que pagaste,{' '}
              <strong className="text-slate-900" data-id="element-331">te devuelvo el dinero.</strong>{' '}
              Sin preguntas. Sin formularios complicados. Sin drama.
            </p>
            <p className="font-bold text-slate-900" data-id="element-332">
              Porque estoy tan seguro de que tus hijos van a amar esto, que el
              riesgo es mío, no tuyo.
            </p>
          </FadeIn>
        </div>
      </section>

      {/* SECCIÓN 9 & 10: URGENCIA Y CTA FINAL */}
      <section className="py-12 md:py-16 bg-white border-t border-slate-200" data-id="element-333">
        <div className="container mx-auto px-4 max-w-4xl text-center" data-id="element-334">
          <FadeIn data-id="element-335">
            <div className="inline-flex items-center gap-2 bg-red-100 text-red-700 px-5 py-2 rounded-full font-bold mb-6" data-id="element-336">
              <Clock className="w-5 h-5" data-id="element-337" /> ⏳ Las vacaciones arrancan el 6 de
              julio. ¿Vas a esperar al último momento?
            </div>

            <div className="mb-8" data-id="element-338">
              <Countdown data-id="element-339" />
            </div>

            <p className="text-slate-600 mb-8 max-w-2xl mx-auto" data-id="element-340">
              Cada año pasa lo mismo: los padres que planifican con anticipación
              tienen las mejores vacaciones. Los que improvisan terminan
              cediendo al celular desde el día uno.{' '}
              <strong className="text-slate-900" data-id="element-341">
                Este kit es para los padres que deciden hoy.
              </strong>
            </p>

            <div className="grid sm:grid-cols-3 gap-3 max-w-3xl mx-auto mb-10 text-left" data-id="element-342">
              <div className="bg-red-50 border border-red-100 rounded-2xl p-4 flex gap-2" data-id="element-343">
                <span className="text-lg" data-id="element-344">🔴</span>
                <p className="text-xs font-medium text-red-900" data-id="element-345">
                  Precio de lanzamiento de US$9 (vale US$149) por tiempo
                  limitado.
                </p>
              </div>
              <div className="bg-red-50 border border-red-100 rounded-2xl p-4 flex gap-2" data-id="element-346">
                <span className="text-lg" data-id="element-347">🔴</span>
                <p className="text-xs font-medium text-red-900" data-id="element-348">
                  Los bonus se retiran después del lanzamiento.
                </p>
              </div>
              <div className="bg-red-50 border border-red-100 rounded-2xl p-4 flex gap-2" data-id="element-349">
                <span className="text-lg" data-id="element-350">🔴</span>
                <p className="text-xs font-medium text-red-900" data-id="element-351">
                  Acceso inmediato: lo comprás ahora y lo tenés en 5 minutos.
                </p>
              </div>
            </div>

            <h2 className="text-3xl md:text-5xl font-black mb-5" data-id="element-352">
              🧊 ¿LISTO PARA SALVAR LAS VACACIONES DE INVIERNO?
            </h2>

            <p className="text-lg text-slate-600 mb-6 max-w-2xl mx-auto" data-id="element-353">
              Dentro de unos días tus hijos van a estar en casa, mirándote con
              esa cara. Vos podés estar preparado. Con un plan. Con 1.500 ideas
              en la mano.
            </p>

            <div className="relative mx-auto max-w-2xl mb-8" data-id="element-354">
              <div className="absolute -inset-4 bg-accent-500/15 blur-3xl rounded-[2.5rem]" data-id="element-355"></div>
              <img src="https://cdn.magicpatterns.com/uploads/9DJZpmfqhg9ea7uZKrrCkg/ChatGPT_Image_19_jun_2026,_05_18_27_p.m..png" alt="Familia feliz mirando juntos cómo erupciona el volcán de bicarbonato en la mesa de casa" className="relative w-full rounded-3xl shadow-2xl" loading="lazy" data-id="element-356" />
            </div>

            <div className="bg-slate-50 p-6 rounded-3xl border border-slate-200 max-w-2xl mx-auto mb-8 text-left" data-id="element-357">
              <h3 className="font-bold text-lg mb-4 text-center" data-id="element-358">
                🎯 LO QUE PASA CUANDO HACÉS CLIC:
              </h3>
              <ul className="space-y-3" data-id="element-359">
                <li className="flex items-center gap-2" data-id="element-360">
                  <Zap className="w-5 h-5 text-accent-500" data-id="element-361" /> Accedés al pago
                  seguro
                </li>
                <li className="flex items-center gap-2" data-id="element-362">
                  <Zap className="w-5 h-5 text-accent-500" data-id="element-363" /> En menos de 5
                  minutos tenés el kit en tu correo
                </li>
                <li className="flex items-center gap-2" data-id="element-364">
                  <Zap className="w-5 h-5 text-accent-500" data-id="element-365" /> Lo abrís, imprimís
                  lo que necesites y empezás
                </li>
                <li className="flex items-center gap-2" data-id="element-366">
                  <Zap className="w-5 h-5 text-accent-500" data-id="element-367" /> Tus hijos ya
                  tienen plan para las 2 semanas
                </li>
              </ul>
            </div>

            <CTAButton className="w-full md:w-auto text-lg md:text-xl py-4 md:py-5" data-id="element-368" />
            <p className="mt-4 text-xs text-slate-500 font-medium" data-id="element-369">
              Acceso inmediato • Descarga digital • Garantía incluida
            </p>
          </FadeIn>
        </div>
      </section>

      {/* SECCIÓN 11: FAQ */}
      <section className="py-12 md:py-16 bg-slate-50 border-t border-slate-200" data-id="element-370">
        <div className="container mx-auto px-4" data-id="element-371">
          <FadeIn data-id="element-372">
            <h2 className="text-2xl md:text-3xl font-black text-center mb-8" data-id="element-373">
              Preguntas Frecuentes
            </h2>
            <FAQ data-id="element-374" />
          </FadeIn>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-400 py-6 text-center text-xs" data-id="element-375">
        <p data-id="element-376">© 2026 Kit Anti-Aburrimiento. Todos los derechos reservados.</p>
      </footer>
    </div>;
}